import type { BoardData, Channel, Connection } from '../model/schema.js';
import {
  findPort,
  inputForOutput,
  isPassThrough,
  isTrunk,
  itemName,
  lanePorts,
  outputsForInput,
  resolvePatch,
} from './ports.js';
import type { NodeRef, TraceHop, TraceStart } from './types.js';

type Direction = 'forward' | 'backward';
type PortNode = Extract<NodeRef, { type: 'port' }>;

/** A step of the walk: the node to visit, the hop that led there, and whether internal routing may be applied. */
interface Step {
  node: NodeRef;
  hop?: TraceHop;
  internal: boolean;
}

/** Stable identity of a node (lanes distinguish trunk lanes); independent of property order. */
export function nodeKey(node: NodeRef): string {
  const entries = Object.entries(node).sort(([a], [b]) => (a < b ? -1 : 1));
  return JSON.stringify(entries);
}

function portNode(data: BoardData, itemId: string, portId: string, lane?: number): PortNode {
  const port = findPort(data, itemId, portId);
  return lane !== undefined && port !== undefined && isTrunk(port)
    ? { type: 'port', itemId, portId, lane }
    : { type: 'port', itemId, portId };
}

function startNode(data: BoardData, start: TraceStart): NodeRef {
  if ('channelId' in start) return { type: 'channel', channelId: start.channelId };
  if ('mixId' in start) return { type: 'mix', mixId: start.mixId };
  if ('text' in start) return { type: 'text', text: start.text };
  if (start.portId === undefined) return { type: 'item', itemId: start.itemId };
  return portNode(data, start.itemId, start.portId, start.lane);
}

function connectionLabel(c: Connection): string {
  return c.label ?? c.cableType ?? 'cable';
}

function viaLabel(data: BoardData, c: Connection): string | undefined {
  if (c.via === undefined) return undefined;
  const sub = data.items[c.via.itemId];
  return `${sub === undefined ? 'lane' : itemName(sub)} #${c.via.lane}`;
}

/** A cable may be walked against its drawn direction only when both ends are bidirectional. */
function reversible(data: BoardData, c: Connection): boolean {
  const a = findPort(data, c.from.itemId, c.from.portId);
  const b = findPort(data, c.to.itemId, c.to.portId);
  return a?.direction === 'bi' && b?.direction === 'bi';
}

function connectionSteps(data: BoardData, node: PortNode, dir: Direction): Step[] {
  const steps: Step[] = [];
  const at = (end: Connection['from']): boolean =>
    end.itemId === node.itemId && end.portId === node.portId;
  for (const c of Object.values(data.connections)) {
    const along = dir === 'forward' ? at(c.from) : at(c.to);
    const against = (dir === 'forward' ? at(c.to) : at(c.from)) && reversible(data, c);
    const others = [
      ...(along ? [dir === 'forward' ? c.to : c.from] : []),
      ...(against ? [dir === 'forward' ? c.from : c.to] : []),
    ];
    for (const end of others) {
      const other = portNode(data, end.itemId, end.portId, node.lane);
      // A lane only travels along trunk cables; it cannot be followed into a plain port.
      if (node.lane !== undefined && other.lane === undefined) continue;
      const via = viaLabel(data, c);
      const hop: TraceHop = {
        kind: 'formal',
        from: dir === 'forward' ? node : other,
        to: dir === 'forward' ? other : node,
        label: connectionLabel(c),
      };
      if (via !== undefined) hop.via = via;
      steps.push({ node: other, hop, internal: true });
    }
  }
  return steps;
}

function internalSteps(data: BoardData, node: PortNode, dir: Direction, internal: boolean): Step[] {
  const item = data.items[node.itemId];
  if (item === undefined || !isPassThrough(item)) return [];
  const { inputs, outputs, trunks } = lanePorts(item);
  const label = `${itemName(item)} internal`;
  const targets: PortNode[] = [];
  const inIndex = inputs.findIndex((p) => p.id === node.portId);
  const outIndex = outputs.findIndex((p) => p.id === node.portId);
  if (dir === 'forward') {
    if (inIndex >= 0 && trunks.length > 0) {
      for (const t of trunks)
        targets.push({ type: 'port', itemId: item.id, portId: t.id, lane: inIndex + 1 });
    } else if (inIndex >= 0) {
      for (const j of outputsForInput(inputs.length, outputs.length, inIndex)) {
        targets.push({ type: 'port', itemId: item.id, portId: outputs[j]!.id });
      }
    } else if (internal && node.lane !== undefined) {
      const out = outputs[node.lane - 1];
      if (out !== undefined) targets.push({ type: 'port', itemId: item.id, portId: out.id });
    }
  } else if (outIndex >= 0 && trunks.length > 0) {
    for (const t of trunks)
      targets.push({ type: 'port', itemId: item.id, portId: t.id, lane: outIndex + 1 });
  } else if (outIndex >= 0) {
    const k = inputForOutput(inputs.length, outputs.length, outIndex);
    if (k !== undefined) targets.push({ type: 'port', itemId: item.id, portId: inputs[k]!.id });
  } else if (internal && node.lane !== undefined) {
    const input = inputs[node.lane - 1];
    if (input !== undefined) targets.push({ type: 'port', itemId: item.id, portId: input.id });
  }
  return targets.map((t) => ({
    node: t,
    hop: {
      kind: 'formal',
      from: dir === 'forward' ? node : t,
      to: dir === 'forward' ? t : node,
      label,
    },
    internal: false,
  }));
}

/** The source of a channel: formal (existing item) or informal (patch / free text). */
export function channelSourceSteps(data: BoardData, ch: Channel): TraceHop[] {
  const to: NodeRef = { type: 'channel', channelId: ch.id };
  const source = data.items[ch.sourceItemId ?? ''];
  if (source !== undefined) {
    const from: NodeRef =
      ch.sourcePortId === undefined
        ? { type: 'item', itemId: source.id }
        : portNode(data, source.id, ch.sourcePortId);
    return [{ kind: 'formal', from, to, label: 'source' }];
  }
  const hops: TraceHop[] = [];
  let sink: NodeRef = to;
  if (ch.patch) {
    const resolved = resolvePatch(data, ch.patch);
    const from: NodeRef =
      resolved === undefined
        ? { type: 'text', text: ch.patch }
        : { type: 'port', itemId: resolved.itemId, portId: resolved.portId };
    hops.push({ kind: 'informal', from, to: sink, label: 'patch' });
    if (resolved !== undefined) return hops;
    sink = from;
  }
  if (ch.sourceText) {
    hops.push({
      kind: 'informal',
      from: { type: 'text', text: ch.sourceText },
      to: sink,
      label: 'source',
    });
  }
  return hops;
}

function sameNode(a: NodeRef, b: NodeRef): boolean {
  return nodeKey(a) === nodeKey(b);
}

/** Output (or bidirectional) ports also act for their item when a channel names only the item. */
function itemLevelAlias(data: BoardData, node: NodeRef): NodeRef | undefined {
  if (node.type !== 'port') return undefined;
  const port = findPort(data, node.itemId, node.portId);
  return port !== undefined && port.direction !== 'in'
    ? { type: 'item', itemId: node.itemId }
    : undefined;
}

/** Channel source hops leaving `node` (forward direction): node → channel, or text → patch text. */
function channelSteps(data: BoardData, node: NodeRef): Step[] {
  const alias = itemLevelAlias(data, node);
  return Object.values(data.channels).flatMap((ch) =>
    channelSourceSteps(data, ch)
      .filter(
        (hop) => sameNode(hop.from, node) || (alias !== undefined && sameNode(hop.from, alias)),
      )
      .map((hop) => ({ node: hop.to, hop, internal: true })),
  );
}

function mixesFeeding(data: BoardData, itemId: string): Step[] {
  return Object.values(data.mixes)
    .filter((m) => m.outputItemIds.includes(itemId))
    .map((m) => {
      const node: NodeRef = { type: 'mix', mixId: m.id };
      return {
        node,
        hop: { kind: 'formal', from: node, to: { type: 'item', itemId }, label: 'mix output' },
        internal: true,
      };
    });
}

function sendLabel(level: number | undefined): string {
  return level === undefined ? 'send' : `send ${level} dB`;
}

function forwardSteps(data: BoardData, step: Step): Step[] {
  const { node } = step;
  switch (node.type) {
    case 'port':
      return [
        ...connectionSteps(data, node, 'forward'),
        ...internalSteps(data, node, 'forward', step.internal),
        ...channelSteps(data, node),
      ];
    case 'item': {
      const ports = (data.items[node.itemId]?.ports ?? []).filter((p) => p.direction !== 'in');
      return [
        ...channelSteps(data, node),
        ...ports.map((p) => ({ node: portNode(data, node.itemId, p.id), internal: true })),
      ];
    }
    case 'channel':
      return Object.values(data.mixes).flatMap((m) =>
        m.sends
          .filter((s) => s.channelId === node.channelId)
          .map((s) => {
            const to: NodeRef = { type: 'mix', mixId: m.id };
            return {
              node: to,
              hop: { kind: 'formal', from: node, to, label: sendLabel(s.level) },
              internal: true,
            };
          }),
      );
    case 'mix': {
      const mix = data.mixes[node.mixId];
      return (mix?.outputItemIds ?? [])
        .filter((id) => data.items[id] !== undefined)
        .map((itemId) => {
          const to: NodeRef = { type: 'item', itemId };
          return {
            node: to,
            hop: { kind: 'formal', from: node, to, label: 'mix output' },
            internal: true,
          };
        });
    }
    case 'text':
      return channelSteps(data, node);
  }
}

function backwardSteps(data: BoardData, step: Step): Step[] {
  const { node } = step;
  switch (node.type) {
    case 'port': {
      const port = findPort(data, node.itemId, node.portId);
      return [
        ...connectionSteps(data, node, 'backward'),
        ...internalSteps(data, node, 'backward', step.internal),
        ...(port !== undefined && port.direction !== 'out' ? mixesFeeding(data, node.itemId) : []),
      ];
    }
    case 'item': {
      const ports = (data.items[node.itemId]?.ports ?? []).filter((p) => p.direction !== 'out');
      return [
        ...mixesFeeding(data, node.itemId),
        ...ports.map((p) => ({ node: portNode(data, node.itemId, p.id), internal: true })),
      ];
    }
    case 'channel': {
      const ch = data.channels[node.channelId];
      return (ch === undefined ? [] : channelSourceSteps(data, ch)).map((hop) => ({
        node: hop.from,
        hop,
        internal: true,
      }));
    }
    case 'mix': {
      const mix = data.mixes[node.mixId];
      return (mix?.sends ?? [])
        .filter((s) => data.channels[s.channelId] !== undefined)
        .map((s) => {
          const from: NodeRef = { type: 'channel', channelId: s.channelId };
          return {
            node: from,
            hop: { kind: 'formal', from, to: node, label: sendLabel(s.level) },
            internal: true,
          };
        });
    }
    case 'text':
      return Object.values(data.channels).flatMap((ch) =>
        channelSourceSteps(data, ch)
          .filter((hop) => sameNode(hop.to, node))
          .map((hop) => ({ node: hop.from, hop, internal: true })),
      );
  }
}

function walk(
  data: BoardData,
  start: TraceStart,
  next: (data: BoardData, step: Step) => Step[],
): TraceHop[] {
  const hops: TraceHop[] = [];
  const visited = new Set<string>();
  const seenHops = new Set<string>();
  const stack: Step[] = [{ node: startNode(data, start), internal: true }];
  while (stack.length > 0) {
    const step = stack.pop()!;
    if (step.hop !== undefined) {
      const from = nodeKey(step.hop.from);
      const to = nodeKey(step.hop.to);
      const key = `${from}>${to}`;
      // Skip duplicates and the way back over a bidirectional cable.
      if (seenHops.has(key) || seenHops.has(`${to}>${from}`)) continue;
      seenHops.add(key);
      hops.push(step.hop);
    }
    const key = nodeKey(step.node);
    // Stryker disable next-line ConditionalExpression,BlockStatement: equivalent – re-expanding a visited node only yields hops already in seenHops (no output change).
    if (visited.has(key)) continue;
    visited.add(key);
    stack.push(...next(data, step).reverse());
  }
  return hops;
}

/**
 * Follows the signal downstream from `start`: cables, stagebox/sub-core/DI pass-through, trunk
 * lanes, channels (formal source or informal patch), mix sends and mix outputs. Each hop is
 * reported once; cycles terminate.
 */
export function traceForward(data: BoardData, start: TraceStart): TraceHop[] {
  return walk(data, start, forwardSteps);
}

/** Follows the signal upstream from `start`. Hops keep signal direction (`from` = upstream). */
export function traceBackward(data: BoardData, start: TraceStart): TraceHop[] {
  return walk(data, start, backwardSteps);
}
